import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import moment from "moment";
import clsx from "clsx";
// eslint-disable-next-line import/no-extraneous-dependencies
import { sub, subDays } from "date-fns";
import {
  DateRange as DateRangeRaw,
  DefinedRange as DefinedRangeRaw,
} from "react-date-range";
import * as locales from "react-date-range/dist/locale";
import { KButton, KIcon } from "@kyburz/kdesign-system";
import {ClickAwayListener} from "@material-ui/core";
import { useStyles } from "./styles";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import TimeButton from "./TimeButton";
import TextInput from "../TextInput/TextInput";

type Selection = {
  selection: {
    startDate: Date;
    endDate: Date;
  };
};

export const capitalize = (text: string = "") =>
  text.charAt(0).toUpperCase() + text.slice(1);

interface Props {
  className?: string;
  label?: string;
  onChange: (value?: any) => void;
  onTimeChange?: (value: any) => void | undefined;
  setApplyClicked?: ([from, to]: [Date, Date]) => void;
  onOverRangeLimit?: () => void;
  disabled?: boolean;
  editable?: boolean;
  startTime: string;
  endTime: string;
  applyText?: string;
  applyButton?: boolean;
  timeActive?: boolean;
  fromDate?: Date;
  toDate?: Date;
  maxDate?: Date;
  minDate?: Date;
  locale?: "de" | "en" | "fr" | "it";
  rangeLimit?: number;
  buttonOnly?: boolean;
  buttonLabel?: string;
  dateTimePickerClassName?: string;
  monthlyView?: boolean;
  hasClock?: boolean;
}

const KDateTimePicker: React.FC<Props> = ({
  className,
  label = undefined,
  onChange,
  disabled = false,
  editable = true,
  setApplyClicked = () => {},
  onTimeChange = () => {},
  onOverRangeLimit = () => {},
  startTime = "00:00",
  endTime = "23:59",
  applyText = "Apply",
  applyButton = true,
  timeActive = true,
  fromDate,
  toDate,
  maxDate = undefined,
  minDate = undefined,
  locale = "de",
  rangeLimit = undefined,
  buttonOnly = false,
  buttonLabel = "",
  dateTimePickerClassName,
  monthlyView = false,
  hasClock = true,
}) => {
  const now = useRef(new Date());
  const [to, setTo] = useState(toDate || now.current);
  const [from, setFrom] = useState(fromDate || subDays(now.current, 0));
  const classes = useStyles();
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [popupCoords, setPopupCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const DateRange = DateRangeRaw as unknown as React.FC<any>;
  const DefinedRange = DefinedRangeRaw as unknown as React.FC<any>;

  useEffect(() => {
    if (fromDate) {
      setFrom(fromDate);
    }
    if (toDate) {
      setTo(toDate);
    }
  }, [fromDate, toDate]);

  const isSameDate = (firstDate: Date, secondDate: Date) =>
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getDate() === secondDate.getDate();

  const handleSelectDateRange = useCallback(
    ({ selection: { startDate, endDate } }: Selection, shouldApply = false) => {
      const daysRange = moment(endDate).diff(startDate, "days") + 1;
      const isOverLimit = rangeLimit && daysRange > rangeLimit;
      if (isOverLimit) {
        const daysOverLimit = daysRange - (rangeLimit || 0);
        endDate = sub(endDate, { days: daysOverLimit });
        onOverRangeLimit();
        if (shouldApply) setApplyClicked([startDate, endDate]);
      }

      setFrom(startDate);
      setTo(endDate);
      onChange([startDate, endDate]);
    },
    [rangeLimit],
  );

  const handleSelectOneDate = useCallback(
    ({ selection: { startDate, endDate } }: Selection) => {
      const newDate = isSameDate(startDate, from) ? endDate : startDate;
      setFrom(newDate);
      setTo(newDate);
      onChange([newDate, newDate]);
    },
    [from],
  );

  // change dates when rangeLimit is less then the range of selected dates
  useEffect(() => {
    handleSelectDateRange(
      { selection: { startDate: from, endDate: to } },
      true,
    );
  }, [rangeLimit]);

  const handleSelect =
    rangeLimit === 1 ? handleSelectOneDate : handleSelectDateRange;

  const onClick = () => {
    if (!visible && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPopupCoords({ top: rect.bottom, left: rect.left });
    }
    setVisible(!visible);
  };

  const onApply = () => {
    setApplyClicked([from, to]);
    setVisible(!visible);
  };

  const ranges = useMemo(() => {
    return [
      {
        startDate: from,
        endDate: to,
        key: "selection",
      },
    ];
  }, [from, to]);

  const generatePreviousMonths = (numMonths: number) => {
    const months: {
      isSelected: () => boolean;
      label: string;
      range: () => { startDate: Date; endDate: Date };
    }[] = [];
    const n = new Date();

    months.push({
      isSelected: () => {
        const definedRange = ranges[0];
        return (
          isSameDate(
            new Date(n.getFullYear(), n.getMonth(), 1),
            definedRange.startDate,
          ) && isSameDate(n, definedRange.endDate)
        );
      },
      label: capitalize(
        n.toLocaleString(locale, {
          month: "long",
          year: "numeric",
        }),
      ),
      range: () => ({
        startDate: new Date(n.getFullYear(), n.getMonth(), 1),
        endDate: n,
      }),
    });

    for (let i = 0; i < numMonths; i += 1) {
      const previousMonthDate = new Date(
        n.getFullYear(),
        n.getMonth() - (i + 1),
        1,
      );
      const l = capitalize(
        previousMonthDate.toLocaleString(locale, {
          month: "long",
          year: "numeric",
        }),
      );

      const startDate = new Date(
        previousMonthDate.getFullYear(),
        previousMonthDate.getMonth(),
        1,
      );
      const endDate = new Date(
        previousMonthDate.getFullYear(),
        previousMonthDate.getMonth() + 1,
        0,
      );

      months.push({
        isSelected: () => {
          const definedRange = ranges[0];
          return (
            isSameDate(startDate, definedRange.startDate) &&
            isSameDate(endDate, definedRange.endDate)
          );
        },
        label: l,
        range: () => ({
          startDate,
          endDate,
        }),
      });
    }

    return months;
  };

  const formattedFrom = useMemo(() => {
    return from
      ? `${moment(from).format("DD.MM.YYYY")}${hasClock ? ` ${startTime}` : ""}`
      : "Start Date";
  }, [from, hasClock, startTime]);

  const formattedTo = useMemo(() => {
    return to
      ? `${moment(to).format("DD.MM.YYYY")}${hasClock ? ` ${endTime}` : ""}`
      : "End Date";
  }, [to, hasClock, endTime]);

  return (
    <ClickAwayListener onClickAway={() => setVisible(false)}>
      <div ref={containerRef} className={clsx(classes.calendar__input, className)}>
        {buttonOnly ? (
          <KButton
            theme="tertiary"
            disabled={disabled || !editable}
            icon={<KIcon icon="calendar" />}
            onClick={onClick}
            style={{ textTransform: "capitalize" }}
          >
            {buttonLabel}
          </KButton>
        ) : (
          <TextInput
            disabled={disabled || !editable}
            onClick={onClick}
            type="text"
            icon={{ rightIcon: "calendar" }}
            value={`${formattedFrom} - ${formattedTo}`}
            title={label}
          />
        )}
        {visible && !disabled && (
          <div
            className={`${classes.root} ${dateTimePickerClassName}`}
            style={{
              position: "fixed",
              top: popupCoords.top,
              left: popupCoords.left,
              zIndex: 1400,
              ...(monthlyView ? { paddingBottom: 0 } : undefined),
            }}
          >
            {monthlyView ? (
              <DefinedRange
                ranges={ranges}
                className={`${clsx(classes.calendarPicker)} ${clsx(
                  classes.definedRange,
                )}`}
                staticRanges={generatePreviousMonths(12 * 3)}
                inputRanges={[]}
                onChange={handleSelect as (rangesByKey: any) => void}
              />
            ) : (
              <DateRange
                className={clsx(classes.calendarPicker)}
                moveRangeOnFirstSelection={false}
                ranges={ranges}
                onChange={handleSelect as (rangesByKey: any) => void}
                showDateDisplay={false}
                locale={locales[locale]}
                showPreview={rangeLimit !== 1}
                rangeColors={["#004f8d"]}
                maxDate={maxDate}
                minDate={minDate}
                showMonthAndYearPickers={true}
                showMonthArrow={true}
              />
            )}
            {hasClock && (
              <TimeButton
                applyButton={applyButton}
                timeActive={timeActive}
                setApplyClicked={onApply}
                onTimeChange={onTimeChange}
                startTime={startTime}
                endTime={endTime}
                applyText={applyText}
              />
            )}
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default KDateTimePicker;
